import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form } from 'react-bootstrap';
import InfoBar from '../components/Navigation/InfoBar';
import { getTrainingsForCompany } from '../constants/routes';
import useApi from '../hooks/use-api';
import moment from '../moment';
import EnrollEmployeeModal from '../components/Modal/EnrollEmployeesModal';
import {
	isCode95CcvCode,
	transformTrainingDataForCompany,
	findSoobDiscount,
} from '../utils/training';
import ReservationModal from '../components/Modal/ReservationModal/ReservationModal';
import { CODE_95_ENABLED, useViewPreferences } from '../hooks/view-preferences';
import DateFilters, { isDateRangeValid } from '../components/Tables/Filters/DateFilter';
import useUserPreferences from '../hooks/use-user-preferences';
import { COURSE_LIST_TABLE } from '../constants/tables';
import { useUser } from '../hooks/use-user';
import CourseAgendaOverview from '../components/Tables/CourseList/CourseAgenda/CourseAgendaOverview';

const TABLE_EXTERNAL_FILTERS_NAME = `${COURSE_LIST_TABLE}ExternalFilters`;
const MIN_DATE = moment().subtract(1, 'day');

function CourseListPage() {
	const { t } = useTranslation();
	const { activeCompany } = useUser();
	const { getFilters, setFilters } = useUserPreferences();
	const sessionFilters = getFilters(TABLE_EXTERNAL_FILTERS_NAME);
	const { isPreferenceOptionEnabled } = useViewPreferences();

	const [body, setBody] = useState([]);
	const [showEnrollModal, setShowEnrollModal] = useState(false);
	const [showReservationModal, setShowReservationModal] = useState(false);
	const [selectedTraining, setSelectedTraining] = useState(null);
	const [showCode95Trainings, setShowCode95Trainings] = useState(
		sessionFilters?.showCode95Trainings || false,
	);
	const [showSoobTrainings, setShowSoobTrainings] = useState(
		sessionFilters?.showSoobTrainings || false,
	);

	const defaultStartDate = moment().set({ hour: 0, minute: 0, second: 0 });
	const defaultEndDate = moment().add(1, 'years').set({ hour: 0, minute: 0, second: 0 });

	const initialDateFiltersState = {
		startDate:
			sessionFilters?.dateFilters?.startDate &&
			moment.isMoment(moment(sessionFilters?.dateFilters?.startDate))
				? moment(sessionFilters.dateFilters.startDate).set({ hour: 0, minute: 0, second: 0 })
				: defaultStartDate,
		endDate:
			sessionFilters?.dateFilters?.endDate &&
			moment.isMoment(moment(sessionFilters?.dateFilters?.endDate))
				? moment(sessionFilters.dateFilters.endDate).set({ hour: 0, minute: 0, second: 0 })
				: defaultEndDate,
	};

	const [dateFilters, setDateFilters] = useState(initialDateFiltersState);

	const code95Enabled = isPreferenceOptionEnabled(CODE_95_ENABLED);

	const [{ data, isLoading }, fetchTrainings] = useApi.get(
		getTrainingsForCompany(activeCompany.id),
		{},
	);

	useEffect(() => {
		if (data) {
			checkData(data);
		}
	}, [data]);

	useEffect(() => {
		updateFilters();
		if (data) {
			checkData(data);
		}
	}, [showCode95Trainings, showSoobTrainings]);

	const checkData = () => {
		const trainingsToShow = [];
		data.forEach((training) => {
			if (!Object.prototype.hasOwnProperty.call(training, 'soobDiscount')) {
				training.soobDiscount = findSoobDiscount(training);
			}
			if (!showCode95Trainings && !showSoobTrainings) {
				trainingsToShow.push(training);
				return;
			}

			if (showCode95Trainings) {
				if (showSoobTrainings) {
					if (
						training.ccvCode?.length &&
						isCode95CcvCode(training.ccvCode) &&
						training.soobDiscount
					) {
						trainingsToShow.push(training);
					}
					return;
				}
				if (training.ccvCode?.length && isCode95CcvCode(training.ccvCode)) {
					trainingsToShow.push(training);
				}
			} else if (showSoobTrainings && training.soobDiscount) {
				trainingsToShow.push(training);
			}
		});
		setBody(transformTrainingDataForCompany(trainingsToShow));
	};

	const updateFilters = () => {
		setFilters(TABLE_EXTERNAL_FILTERS_NAME, {
			dateFilters: {
				startDate: dateFilters.startDate.format(),
				endDate: dateFilters.endDate.format(),
			},
			showCode95Trainings,
			showSoobTrainings,
		});
	};

	const refreshTrainings = () => {
		fetchTrainings({
			startDate: dateFilters.startDate.format(),
			endDate: dateFilters.endDate.format(),
		});
	};

	useEffect(() => {
		if (isDateRangeValid(dateFilters.startDate, dateFilters.endDate, { minDate: MIN_DATE })) {
			updateFilters();
			refreshTrainings();
		}
	}, [dateFilters]);

	const resetFilters = () => {
		setDateFilters({
			startDate: defaultStartDate,
			endDate: defaultEndDate,
		});
		setShowCode95Trainings(false);
		setShowSoobTrainings(false);
	};

	return (
		<>
			<InfoBar />
			{data === null ? (
				<div className="loading-spinner" />
			) : (
				<div className="row pb-5">
					<div className="col-sm-12 col-xl-10 mx-auto mt-2">
						<EnrollEmployeeModal
							show={showEnrollModal}
							setShow={setShowEnrollModal}
							training={selectedTraining}
							handleSubmit={refreshTrainings}
						/>
						<ReservationModal
							show={showReservationModal}
							setShow={setShowReservationModal}
							training={selectedTraining}
							setTraining={setSelectedTraining}
							handleSubmit={refreshTrainings}
						/>
						<div className="d-flex mb-3">
							<Card className="col-5">
								<Card.Body>
									<DateFilters
										minDate={MIN_DATE}
										filters={dateFilters}
										handleChange={(dateKey, date) =>
											setDateFilters({ ...dateFilters, [dateKey]: date })
										}
									/>
									<div className="d-flex justify-content-between mt-2">
										{code95Enabled ? (
											<Form.Check className="mt-2" type="checkbox">
												<Form.Check.Input
													type="checkbox"
													checked={showCode95Trainings || false}
													onChange={({ target }) => setShowCode95Trainings(target.checked)}
												/>
												<Form.Check.Label
													onClick={() => setShowCode95Trainings(!showCode95Trainings)}>
													{t('trainings.show_code_95_trainings')}
												</Form.Check.Label>
											</Form.Check>
										) : (
											<></>
										)}
										{activeCompany.soob ? (
											<Form.Check className="mt-2" type="checkbox">
												<Form.Check.Input
													type="checkbox"
													checked={showSoobTrainings || false}
													onChange={({ target }) => setShowSoobTrainings(target.checked)}
												/>
												<Form.Check.Label onClick={() => setShowSoobTrainings(!showSoobTrainings)}>
													{t('trainings.show_soob_trainings')}
												</Form.Check.Label>
											</Form.Check>
										) : (
											<></>
										)}
									</div>
									<div className="text-end mt-2">
										<button
											type="button"
											className="btn btn-outline-secondary"
											onClick={resetFilters}>
											{t('table.clearFilters')}
										</button>
									</div>
								</Card.Body>
							</Card>
						</div>
						{isLoading ? (
							<div className="loading-spinner" />
						) : (
							<>
								<CourseAgendaOverview
									courses={body}
									onEnrollClick={(training) => {
										setSelectedTraining(training);
										setShowEnrollModal(true);
									}}
									onReserveClick={(training) => {
										setSelectedTraining(training);
										setShowReservationModal(true);
									}}
								/>
							</>
						)}
					</div>
				</div>
			)}
		</>
	);
}

export default CourseListPage;
